import { useMemo } from "react";
import { CalendarDays, Route } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { StatusDot } from "@/components/StatusPill";
import { groupAgenda, groupByCustomer, customerRounds } from "@/lib/calendar";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { EventRow, CustomerGroupHeader } from "./EventItem";

const GROUP_TONE = {
  overdue: "text-[rgb(var(--status-overdue))]",
  week: "text-foreground",
  soon: "text-foreground",
  later: "text-muted-foreground",
};

/** Kunderunde-tips: kundar med fleire fristar innan 45 dagar */
const RoundsHint = ({ rounds, onFilterCustomer }) => {
  if (!rounds.length) return null;
  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] p-3 sm:p-4" data-testid="calendar-rounds-hint">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-ink">
          <Route className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-bold">Kunderunde – ta fleire på same tur</div>
          <p className="mt-0.5 text-xs text-muted-foreground">Desse kundane har fleire fristar innan 45 dagar.</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {rounds.map((g) => (
              <button
                key={g.customer_id || "ukjend"}
                type="button"
                onClick={() => onFilterCustomer?.(g.customer_id)}
                className="inline-flex h-9 items-center gap-2 rounded-full border bg-card px-3 text-xs font-semibold shadow-sm transition-colors hover:bg-accent"
                data-testid={`calendar-round-${g.customer_id}`}
              >
                <StatusDot status={g.worst} className="h-2 w-2" />
                <span className="max-w-[180px] truncate">{g.customer_name}</span>
                <span className="rounded-full bg-ink px-1.5 py-0.5 text-[10px] font-bold text-white dark:bg-white dark:text-ink">{g.items.length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AgendaView = ({ events, today, group = "date", onFilterCustomer, onPlan, onMove, onUnplan }) => {
  const dateGroups = useMemo(() => groupAgenda(events, today), [events, today]);
  const customerGroups = useMemo(() => groupByCustomer(events), [events]);
  const rounds = useMemo(() => customerRounds(events, today, 45), [events, today]);

  if (events.length === 0) {
    return (
      <EmptyState
        icon={CalendarDays}
        title="Ingen fristar"
        description="Ingen fristar passar filteret. Legg inn service- eller kontrolldato på maskinene for å sjå dei her."
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="calendar-list-view">
      {group === "date" && <RoundsHint rounds={rounds} onFilterCustomer={onFilterCustomer} />}

      {group === "date"
        ? dateGroups.map((g) => (
            <section key={g.key} data-testid={`calendar-group-${g.key}`}>
              <div className="sticky top-14 z-10 -mx-1 mb-2 flex items-baseline justify-between gap-3 bg-background/90 px-1 py-1.5 backdrop-blur md:top-16">
                <h3 className={cn("font-heading text-sm font-extrabold uppercase tracking-wider", GROUP_TONE[g.key])}>
                  {g.label} <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-foreground">{g.items.length}</span>
                </h3>
                {g.hint && <span className="truncate text-xs text-muted-foreground">{g.hint}</span>}
              </div>
              <div className="space-y-2">
                {g.items.map((e) => (
                  <EventRow key={e.id} e={e} today={today} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
                ))}
              </div>
            </section>
          ))
        : customerGroups.map((g) => (
            <section key={g.customer_id || "ukjend"} className="space-y-2">
              <CustomerGroupHeader g={g} onFilter={onFilterCustomer} />
              <div className="space-y-2 pl-2 sm:pl-3">
                {g.items.map((e) => (
                  <EventRow key={e.id} e={e} today={today} showCustomer={false} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
                ))}
              </div>
              <div className="pl-3 text-[11px] text-muted-foreground">
                Første frist {formatDate(g.first)}
              </div>
            </section>
          ))}
    </div>
  );
};
